import {
    IAxisUnits,
    IChartAxis,
} from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/chart-properties.interface';
import { IEGAxis, ICTCatAx, ICTDateAx, ICTValAx,
    STSchemeColorVal, ICTDispUnits, DeltaOf,
    ICTValAxDelta, ICTCatAxDelta, ICTDateAxDelta,
} from '@tencent/slide-sdk-model';
import { DeepPartial, DeepReadonly } from 'ts-essentials';
import {
    ChartShapeInfoConverter,
} from '@src/slide/domain/ui-model/converter/node-property/module-property/chart/common/chart-shape-info-converter';
import { IShapeModel } from '@src/slide/domain/ui-model/element-modeles/Interface/shape-model.interface';
import {
    ChartTitleConverter,
} from '@src/slide/domain/ui-model/converter/node-property/module-property/chart/title/chart-title-converter';
import {
    ChartLayoutConverter,
} from '@src/slide/domain/ui-model/converter/node-property/module-property/chart/common/chart-layout-converter';
import { setUIValueToSdkData } from '@src/slide/domain/ui-model/converter/node-property/module-property/chart/util';

export const axisTypeMap: Record<keyof IEGAxis, IChartAxis['type']> = {
    catAx: 'category',  // 分类坐标轴
    valAx: 'value', // 值坐标轴
    dateAx: 'date',  // 日期坐标轴
    serAx: 'category',  // todo 这个坐标轴还没有支持，先这么写
};

export class AxisConverter {
    // eslint-disable-next-line complexity
    public static convertAxisDataSdkToUi(
        sdkData: DeepReadonly<ICTCatAx | ICTDateAx | ICTValAx>,
        uiData: IChartAxis,
    ) {
        // -------------------------------- 下面是所有坐标轴都有的公共属性--------------------------------
        // 样式
        ChartShapeInfoConverter.convertPartialDataFromSdkToUi(sdkData, uiData as unknown as IShapeModel);

        // 轴标题
        if (sdkData.title !== undefined) {
            uiData.title = uiData.title || {};
            ChartTitleConverter.convertPartialDataSdkToUi(sdkData.title, uiData.title);
        }

        // 坐标轴位置
        if (sdkData.axPos !== undefined) {
            uiData.position = sdkData.axPos.val;
        }

        // 坐标轴缩放信息
        if (sdkData.scaling !== undefined) {
            uiData.scaling = {};
            if (sdkData.scaling.orientation) {
                uiData.scaling.orientation = sdkData.scaling.orientation.val;
            }
            if (sdkData.scaling.max) {
                uiData.scaling.max = sdkData.scaling.max.val;
            }
            if (sdkData.scaling.min) {
                uiData.scaling.min = sdkData.scaling.min.val;
            }
            if (sdkData.scaling.logBase) {
                uiData.scaling.logBase = sdkData.scaling.logBase.val;
            }
        }

        // 坐标轴label格式化
        if (sdkData.numFmt !== undefined) {
            uiData.numFmt = sdkData.numFmt;
        }

        if (sdkData.majorTickMark !== undefined) {
            uiData.majorTickMark = sdkData.majorTickMark.val;
        }
        if (sdkData.minorTickMark !== undefined) {
            uiData.minorTickMark = sdkData.minorTickMark.val;
        }

        if (sdkData.majorGridlines !== undefined) {
            uiData.majorGridlines = uiData.majorGridlines || {};
            ChartShapeInfoConverter.convertPartialDataFromSdkToUi(
                sdkData.majorGridlines,
                uiData.majorGridlines as IShapeModel,
            );

            this.clearBorderDefaultColorIfFillPhclr(sdkData.majorGridlines.spPr, uiData.majorGridlines.props);
        }
        if (sdkData.minorGridlines !== undefined) {
            uiData.minorGridlines = uiData.minorGridlines || {};
            ChartShapeInfoConverter.convertPartialDataFromSdkToUi(
                sdkData.minorGridlines,
                uiData.minorGridlines as IShapeModel,
            );

            this.clearBorderDefaultColorIfFillPhclr(sdkData.minorGridlines.spPr, uiData.minorGridlines.props);
        }

        if (sdkData.tickLblPos !== undefined) {
            uiData.tickLabelPosition = sdkData.tickLblPos.val;
        }

        if (sdkData.crosses !== undefined) {
            uiData.crosses = sdkData.crosses.val;
        }
        if (sdkData.crossAx !== undefined) {
            uiData.crossAx = sdkData.crossAx.val;
        }

        if (sdkData.axId?.val !== undefined) {
            uiData.axId = sdkData.axId.val;
        }

        if (sdkData.deleteKeyword !== undefined) {
            uiData.isDelete = sdkData.deleteKeyword.val;
        }

        // -------------------------------- 下面是非公共属性--------------------------------

        // 显示单位
        const sdkDispUnits = (sdkData as ICTValAx).dispUnits;   // 这么写是为了ts传参判定不为Undefined，以及不重复写as
        if (sdkDispUnits !== undefined) {
            uiData.units = uiData.units || {};
            this.convertDisplayUnitsSdkToUi(sdkDispUnits, uiData.units);
        }
        // 主坐标轴单位
        const sdkMajorUnit = (sdkData as ICTValAx | ICTDateAx).majorUnit;
        if (sdkMajorUnit !== undefined) {
            uiData.majorUnit = sdkMajorUnit.val;
        }
        // 次坐标轴单位
        const sdkMinorUnit = (sdkData as ICTValAx | ICTDateAx).minorUnit;
        if (sdkMinorUnit !== undefined) {
            uiData.minorUnit = sdkMinorUnit.val;
        }

        const sdkCrossBetween = (sdkData as ICTValAx).crossBetween;
        if (sdkCrossBetween !== undefined) {
            uiData.crossBetween = sdkCrossBetween.val;
        }

        const sdkLblOffset = (sdkData as ICTCatAx | ICTDateAx).lblOffset;
        if (sdkLblOffset !== undefined) {
            uiData.lblOffset = sdkLblOffset.val;
        }

        const sdkAuto = (sdkData as ICTCatAx | ICTDateAx).autoKeyword;
        if (sdkAuto !== undefined) {
            uiData.autoLabeled = sdkAuto.val;
        }

        const sdkLblAlgn = (sdkData as ICTCatAx).lblAlgn;
        if (sdkLblAlgn !== undefined) {
            uiData.lblAlgn = sdkLblAlgn.val;
        }
    }

    // eslint-disable-next-line complexity
    public static convertAxisDataUiToSdk(
        uiData: DeepPartial<IChartAxis>,
        sdkData: DeltaOf<ICTCatAx | ICTDateAx | ICTValAx>,
    ) {
        // -------------------------------- 下面是所有坐标轴都有的公共属性--------------------------------
        // 样式
        ChartShapeInfoConverter.convertPartialDataFromUiToSdk(uiData as unknown as IShapeModel, sdkData);

        // 轴标题
        if (uiData.title !== undefined) {
            if (!uiData.title) {
                sdkData.title = null;
            } else {
                sdkData.title = sdkData.title ?? {};
                ChartTitleConverter.convertPartialDataUiToSdk(uiData.title, sdkData.title);
            }
        }

        // 坐标轴位置
        setUIValueToSdkData(sdkData, 'axPos', uiData.position);

        // 坐标轴缩放信息
        if (uiData.scaling !== undefined) {
            sdkData.scaling = sdkData.scaling ?? {};
            setUIValueToSdkData(sdkData.scaling, 'orientation', uiData.scaling.orientation);
            setUIValueToSdkData(sdkData.scaling, 'max', uiData.scaling.max);
            setUIValueToSdkData(sdkData.scaling, 'min', uiData.scaling.min);
            setUIValueToSdkData(sdkData.scaling, 'logBase', uiData.scaling.logBase);
        }

        // 坐标轴label格式化
        setUIValueToSdkData(sdkData, 'numFmt', uiData.numFmt, false);
        setUIValueToSdkData(sdkData, 'majorTickMark', uiData.majorTickMark);
        setUIValueToSdkData(sdkData, 'minorTickMark', uiData.minorTickMark);
        setUIValueToSdkData(sdkData, 'tickLblPos', uiData.tickLabelPosition);
        setUIValueToSdkData(sdkData, 'crosses', uiData.crosses);
        setUIValueToSdkData(sdkData, 'crossAx', uiData.crossAx);
        setUIValueToSdkData(sdkData, 'axId', uiData.axId);
        setUIValueToSdkData(sdkData, 'deleteKeyword', uiData.isDelete);

        if (uiData.minorGridlines !== undefined) {
            if (!uiData.minorGridlines) {
                sdkData.minorGridlines = null;
            } else {
                sdkData.minorGridlines = sdkData.minorGridlines ?? {};
                ChartShapeInfoConverter.convertPartialDataFromUiToSdk(
                    uiData.minorGridlines,
                    sdkData.minorGridlines,
                );
            }
        }
        if (uiData.majorGridlines !== undefined) {
            if (!uiData.majorGridlines) {
                sdkData.majorGridlines = null;
            } else {
                sdkData.majorGridlines = sdkData.majorGridlines ?? {};
                ChartShapeInfoConverter.convertPartialDataFromUiToSdk(
                    uiData.majorGridlines,
                    sdkData.majorGridlines,
                );
            }
        }

        // -------------------------------- 下面是非公共属性--------------------------------
        // 显示单位
        if (uiData.units !== undefined) {
            if (!uiData.units) {
                (sdkData as ICTValAxDelta).dispUnits = null;
            } else {
                (sdkData as ICTValAxDelta).dispUnits = (sdkData as ICTValAxDelta).dispUnits ?? {};
                this.convertDisplayUnitsUiToSdk(uiData.units, (sdkData as ICTValAxDelta).dispUnits as ICTDispUnits);
            }
        }

        // 主坐标轴单位
        setUIValueToSdkData(sdkData as (ICTValAxDelta | ICTDateAxDelta), 'majorUnit', uiData.majorUnit);
        // 次坐标轴单位
        setUIValueToSdkData(sdkData as (ICTValAxDelta | ICTDateAxDelta), 'minorUnit', uiData.minorUnit);
        setUIValueToSdkData(sdkData as ICTValAxDelta, 'crossBetween', uiData.crossBetween);
        setUIValueToSdkData(sdkData as (ICTCatAxDelta | ICTDateAxDelta), 'lblOffset', uiData.lblOffset);
        setUIValueToSdkData(sdkData as ICTCatAxDelta, 'lblAlgn', uiData.lblAlgn);
        setUIValueToSdkData(sdkData as (ICTCatAxDelta | ICTDateAxDelta), 'autoKeyword', uiData.autoLabeled);
    }

    // 上面的逻辑其他的都是简单赋值，逻辑这个逻辑有点多，所以单独提了出来。后面看是不是把上面的逻辑也拆一下
    public static convertDisplayUnitsSdkToUi(sdkData: DeepReadonly<ICTDispUnits>, uiData: IAxisUnits) {
        if (sdkData.builtInUnit?.val !== undefined) {
            uiData.unit = sdkData.builtInUnit.val;
        }

        if (sdkData.dispUnitsLbl !== undefined) {
            uiData.label = uiData.label || {};

            ChartShapeInfoConverter.convertPartialDataFromSdkToUi(sdkData.dispUnitsLbl, uiData.label as IShapeModel);

            if (sdkData.dispUnitsLbl.layout !== undefined) {
                uiData.label.layout = uiData.label.layout || {};
                ChartLayoutConverter.convertPartialDataFromSdkToUi(sdkData.dispUnitsLbl.layout, uiData.label.layout);
            }
        }
    }

    public static convertDisplayUnitsUiToSdk(uiData: DeepPartial<IAxisUnits>, sdkData: DeltaOf<ICTDispUnits>) {
        if (uiData.unit !== undefined) {
            if (!uiData.unit) {
                sdkData.builtInUnit = null;
            } else {
                sdkData.builtInUnit = { val: uiData.unit };
            }
        }

        if (uiData.label !== undefined) {
            if (!uiData.label) {
                sdkData.dispUnitsLbl = null;
            } else {
                sdkData.dispUnitsLbl = sdkData.dispUnitsLbl ?? {};
                ChartShapeInfoConverter
                    .convertPartialDataFromUiToSdk(uiData.label as IShapeModel, sdkData.dispUnitsLbl);

                if (uiData.label.layout !== undefined) {
                    sdkData.dispUnitsLbl.layout = sdkData.dispUnitsLbl.layout || {};
                    ChartLayoutConverter.convertPartialDataFromUiToSdk(
                        uiData.label.layout,
                        sdkData.dispUnitsLbl.layout,
                    );
                }
            }
        }
    }

    private static clearBorderDefaultColorIfFillPhclr(sdkSpPr, uiProps) {
        // @ts-ignore 这里暂时没办法处理，图表的phClr的样式可能在style.xml里面，其规则还需要逆向，而且导入未解析，如果不处理solidFill继承会是黑色
        // 这里先这么处理一下，让能够走到业务中的兜底值
        const isSdkFillPhClr = sdkSpPr?.ln?.egLineFillProperties?.data?.solidFill?.egColorChoice?.schemeClr
            === STSchemeColorVal.phClr;
        // 部分数据后面是8位的，兼容一下
        const isSdkDefaultClrVal = uiProps?.style?.border?.color === '#000000' || uiProps?.style?.border?.color === '#000000ff';

        if (isSdkDefaultClrVal && isSdkFillPhClr) {
            uiProps.style.border.color = '';
        }
    }
}
